import { useState, useRef, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import { confirmAlert } from "react-confirm-alert";
import { Mic, Paperclip, X, FileIcon } from "lucide-react";
import Select from "react-select";
import ReactQuill from "react-quill";
import { NoteAudioRecorder } from "@/components/Notas/sections/NoteAudioRecorder";
import { AudioPlayer } from "@/components/MenuUtilidades/Chat/components/AudioPlayer";
import { useTranslation } from "react-i18next";
import { useLayout } from "@/contexts/UseLayout";
import TemPermissao from "@/components/utils/NiveisDeAcesso";
import { AnimationOpen } from "@/components/utils/AnimationOpen";
import { ComentariosTarefa } from "@/components/MenuUtilidades/Tarefas/components/ComentariosTarefa";
import { ServiceResumoDesk } from "@/services/serviceResumoDesk";
import ImageGeneric from "@/assets/images/lg.png";
import {
  IconCriarNovaTarefa,
  IconDelete,
  IconEdit,
  IconIsClose,
  IconIsOpen,
} from "@/pages/UserGoOn/Desks/ResumoDesk/Icons";
import { NotaRapidaDropdown } from "@/components/Notas/NotaRapidaDropdown";

interface NotasProps {
  data: any;
  type: string;
  departamentos: any[];
  departamentoActive: number;
}

const isHtmlContent = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

const getDescriptionText = (value?: string) =>
  (value || "")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const hasDescriptionContent = (value?: string) =>
  getDescriptionText(value).length > 0;

export function Notas({
  data,
  type,
  departamentos,
  departamentoActive,
}: NotasProps) {
  const { t } = useTranslation();
  const context = useLayout();
  const queryClient = useQueryClient();
  const noteType = type === "recebimento" ? "pagamento" : type;
  const permissionModule =
    type === "campanha"
      ? "campanhas"
      : type === "cronograma"
        ? "notas"
        : "desks";

  const [newNote, setNewNote] = useState(false);
  const [notaIsOpen, setNotaIsOpen] = useState<Record<string, boolean>>({});
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [existingApiFile, setExistingApiFile] = useState<any>(null);
  const [removedExistingFile, setRemovedExistingFile] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (audioBlob) {
      const url = URL.createObjectURL(audioBlob);
      setAudioBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAudioBlobUrl(null);
    }
  }, [audioBlob]);

  const { control, register, handleSubmit, reset, setValue, watch } = useForm();

  const queryKey = ["notas", noteType, data.id, departamentoActive];

  const {
    data: notasData,
    isLoading: loading,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await ServiceResumoDesk.listNotas({
        type: noteType,
        id: data.id,
        id_departamento: departamentoActive,
      });
      return res?.data ?? null;
    },
    retry: 1,
    staleTime: 1000 * 60 * 2,
  });

  const invalidateNotes = () =>
    queryClient.invalidateQueries({
      queryKey: ["notas", noteType, data.id, departamentoActive],
    });

  const onSubmit = async (payload: any) => {
    const hasDescription = hasDescriptionContent(payload.description);
    const hasSelectedFiles = !!(selectedFiles && selectedFiles.length > 0);
    const hasExistingApiFile = !!(
      isEdit &&
      existingApiFile &&
      !removedExistingFile
    );

    if (
      !(hasDescription || audioBlob || hasSelectedFiles || hasExistingApiFile)
    ) {
      toast.warning(
        "Adicione uma descrição, grave um áudio ou anexe um arquivo.",
      );
      return;
    }

    if (noteType === "desk") payload.id_desk = data.id;
    if (noteType === "reunion") payload.id_reunion = data.id;
    if (noteType === "prospecting") payload.id_prospecting = data.id;
    if (noteType === "pagamento") payload.id_pagamento = data.id;
    if (noteType === "campanha") payload.id_campanha = data.id;
    if (noteType === "cronograma") payload.id_social_media_post = data.id;
    payload.type = noteType;
    payload.id_departamento =
      noteType === "cronograma"
        ? undefined
        : isEdit
          ? (payload.id_departamento ?? departamentoActive)
          : departamentoActive;
    payload.status = isEdit ? (payload.status ?? 1) : 1;
    if (payload.list_responsaveis && Array.isArray(payload.list_responsaveis)) {
      payload.list_responsaveis = payload.list_responsaveis.map(
        (item: any) => item?.value,
      );
    }

    if (!hasDescription && audioBlob) {
      payload.description = "Áudio";
    }
    if (!hasDescription && selectedFiles) {
      payload.description = "Arquivo(s) anexado(s)";
    }

    const filesOrBlob: FileList | Blob | null =
      audioBlob ?? selectedFiles ?? null;

    if (isEdit && removedExistingFile && !filesOrBlob) {
      payload.remove_file = true;
    }

    try {
      setIsSubmiting(true);
      let res;
      if (isEdit) {
        res = await ServiceResumoDesk.editNotas(
          payload,
          payload.id,
          filesOrBlob ?? undefined,
        );
      } else {
        res = await ServiceResumoDesk.addNotas(
          payload,
          filesOrBlob ?? undefined,
        );
      }
      if (res.status === 200) {
        toast.success(isEdit ? "Nota atualizada!" : "Nota criada!");
        setNewNote(false);
        invalidateNotes();
        reset();
        setSelectedFiles(null);
        setAudioBlob(null);
        setShowAudioRecorder(false);
        setExistingApiFile(null);
        setRemovedExistingFile(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.msg ||
          error?.response?.data?.error ||
          "Erro ao salvar nota",
      );
    } finally {
      setIsSubmiting(false);
      setIsEdit(false);
    }
  };

  function belongsToActiveDepartamento(item: any) {
    if (departamentoActive === undefined || departamentoActive === null) {
      return true;
    }
    if (Number(departamentoActive) === 0) return true;
    return String(item?.id_departamento) === String(departamentoActive);
  }

  const notasFiltred = notasData?.data
    ?.filter(belongsToActiveDepartamento)
    .sort((a: any, b: any) => {
      if (a.status === 2 && b.status !== 2) return 1;
      if (a.status !== 2 && b.status === 2) return -1;
      return 0;
    });

  const toggleOpenDescriptionNote = (id: any) => {
    setNotaIsOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  async function deleteNote(id: any) {
    confirmAlert({
      title: t("common:confirmation"),
      message: t("resumoDesk.notes.delete_confirmation"),
      buttons: [
        {
          label: t("common:yes"),
          onClick: async () => {
            try {
              const res = await ServiceResumoDesk.delNotas(id);
              if (res.status === 200) invalidateNotes();
            } catch (error) {
              console.error("Erro:", error);
              toast.error(t("tasks.errors.edit_task"));
            }
          },
        },
        { label: t("common:cancel"), onClick: () => {} },
      ],
    });
  }

  function editNote(note: any) {
    setNewNote(true);
    setValue("title", note.title);
    setValue("id", note.id);
    setValue("description", note.description);
    setValue("id_departamento", note.id_departamento);
    setValue("status", note.status);
    setValue("is_public", note.is_public);
    if (Array.isArray(note.responsaveis)) {
      setValue(
        "list_responsaveis",
        note.responsaveis.map((r: any) => ({
          value: r.id,
          label: `${r.name || ""} ${r.last_name || ""}`
            .replace(/\s+/g, " ")
            .trim(),
          image: r.filepath || r.filePath || null,
        })),
      );
    } else {
      setValue("list_responsaveis", []);
    }
    const apiFiles = [
      ...(Array.isArray(note.files) ? note.files : []),
      ...(note.file_path ? [note.file_path] : []),
    ];
    setExistingApiFile(apiFiles.length > 0 ? apiFiles[0] : null);
    setRemovedExistingFile(false);
    setIsEdit(true);
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFiles(e.target.files);
    }
  };

  const removeFile = (indexToRemove: number) => {
    if (!selectedFiles) return;
    const dt = new DataTransfer();
    Array.from(selectedFiles).forEach((file, index) => {
      if (index !== indexToRemove) dt.items.add(file);
    });
    setSelectedFiles(dt.files.length > 0 ? dt.files : null);
    if (fileInputRef.current) fileInputRef.current.files = dt.files;
  };

  const handleAudioReady = (blob: Blob) => {
    setAudioBlob(blob);
    setShowAudioRecorder(false);
  };

  const removeAudio = () => {
    setAudioBlob(null);
  };

  const resetForm = () => {
    setNewNote(false);
    setIsEdit(false);
    setSelectedFiles(null);
    setAudioBlob(null);
    setShowAudioRecorder(false);
    setExistingApiFile(null);
    setRemovedExistingFile(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    reset();
  };

  const renderTextWithLinks = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const exactUrlRegex = /^https?:\/\/[^\s]+$/;
    const nodes: ReactNode[] = [];

    text.split(/\r?\n/).forEach((line, lineIndex) => {
      if (lineIndex > 0) {
        nodes.push(<br key={`line-break-${lineIndex}`} />);
      }

      line.split(urlRegex).forEach((part, partIndex) => {
        if (!part) return;

        nodes.push(
          exactUrlRegex.test(part) ? (
            <a
              key={`link-${lineIndex}-${partIndex}`}
              href={part}
              className="text-blue-500 underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {part}
            </a>
          ) : (
            <span key={`text-${lineIndex}-${partIndex}`}>{part}</span>
          ),
        );
      });
    });

    return nodes;
  };

  const renderDescription = (description: string) => {
    if (isHtmlContent(description)) {
      return (
        <div
          className="ql-editor p-0 whitespace-pre-wrap break-words"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      );
    }

    return renderTextWithLinks(description);
  };

  function getNomeDepartamentoById(id: number) {
    const item = departamentos.find((element: any) => element.id == id);
    return item ? item.nome : t("resumoDesk.general");
  }

  const responsaveisList = [
    ...(data?.criador
      ? [
          {
            value: data.criador.id,
            label: `${data.criador.name || ""} ${data.criador.last_name || ""}`
              .replace(/\s+/g, " ")
              .trim(),
            image: data.criador.img || data.criador.url_img || ImageGeneric,
          },
        ]
      : []),
    ...(Array.isArray(data?.responsaveis)
      ? data.responsaveis.map((r: any) => ({
          value: r.id,
          label: `${r.name || ""} ${r.last_name || ""}`
            .replace(/\s+/g, " ")
            .trim(),
          image: r.img || r.url_img || r.filepath || r.filePath || ImageGeneric,
        }))
      : []),
  ];

  return (
    <>
      <div>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-stretch">
          <button
            className="bg-[hsl(0,0%,93%)] flex items-center justify-center text-white gap-1 py-2 px-4 rounded-3xl hover:opacity-80 duration-300 text-sm whitespace-nowrap flex-1"
            onClick={() => setNewNote(true)}
          >
            <IconCriarNovaTarefa /> {t("resumoDesk.notes.create_new_note")}
          </button>
          <NotaRapidaDropdown
            type={type}
            data={data}
            departamentoActive={departamentoActive}
            onNoteCreated={invalidateNotes}
          />
        </div>
        <div className="space-y-3 mt-8">
          {(loading || isFetching) &&
            Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="bg-[#EDEDED] rounded-3xl px-8 py-4 h-16"
              >
                <div className="flex justify-between cursor-pointer">
                  <h2 className="font-medium bg-gray-300 h-6 w-40" />
                  <div className="flex items-center gap-2 text-sm">
                    <span className="bg-gray-300 h-4 w-20 rounded-2xl" />
                    <span className="bg-white px-4 py-1 rounded-2xl font-medium w-24" />
                    <button
                      type="button"
                      className="bg-gray-300 h-5 w-5 rounded-full"
                    />
                  </div>
                </div>
              </div>
            ))}

          {!loading &&
            !isFetching &&
            notasFiltred?.map((e: any) => {
              const noteFiles: any[] = [
                ...(Array.isArray(e.files) ? e.files : []),
                ...(e.file && !Array.isArray(e.files) ? [e.file] : []),
              ];
              return (
                <div
                  className="bg-[#EDEDED] rounded-3xl px-4 sm:px-8 py-4 relative"
                  key={e.id}
                >
                  {Array.isArray(e.responsaveis) &&
                    e.responsaveis.length > 0 && (
                      <div className="absolute -top-5 right-3 flex">
                        {e.responsaveis.map((r: any, idx: number) => (
                          <img
                            key={r.id || idx}
                            src={r.filepath || r.filePath || ImageGeneric}
                            alt={`${r.name || ""} ${r.last_name || ""}`
                              .replace(/\s+/g, " ")
                              .trim()}
                            title={`${r.name || ""} ${r.last_name || ""}`
                              .replace(/\s+/g, " ")
                              .trim()}
                            className="w-7 h-7 rounded-full border-2 border-white shadow cursor-pointer"
                          />
                        ))}
                      </div>
                    )}
                  <div className="flex justify-between pb-2">
                    <h2
                      className="font-medium cursor-pointer text-sm sm:text-base"
                      onClick={() => toggleOpenDescriptionNote(e.id)}
                    >
                      {e.title}
                    </h2>
                    <span className="bg-white px-3 py-px text-azul-400 rounded-2xl text-sm">
                      {getNomeDepartamentoById(e.id_departamento)}
                    </span>
                  </div>
                  {e?.file_path &&
                    (() => {
                      const audioExts = [
                        "webm",
                        "mp3",
                        "wav",
                        "ogg",
                        "m4a",
                        "aac",
                      ];
                      const isAudio = audioExts.some((ext) =>
                        (e?.file_path?.extname || "")
                          .toLowerCase()
                          .endsWith(ext),
                      );
                      return (
                        <div className="flex items-center gap-2 bg-cinza-100 rounded-xl px-3 py-2 my-2">
                          {isAudio ? (
                            <audio
                              src={e?.file_path?.path}
                              controls
                              className="flex-1 h-8"
                              style={{ accentColor: "#4A90D9" }}
                            />
                          ) : (
                            <>
                              <FileIcon
                                size={14}
                                className="text-cinza-500 flex-shrink-0"
                              />
                              <a
                                href={e?.file_path?.path}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {e?.file_path?.name}
                              </a>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  <div className="flex items-center justify-end gap-2 text-xs">
                    <span>
                      {t("resumoDesk.notes.created_at", {
                        date: format(new Date(e.createdAt), "dd/MM/yy - HH:mm"),
                      })}{" "}
                      {t("resumoDesk.notes.by")}
                    </span>
                    <span className="bg-white px-4 py-1 rounded-2xl font-medium">
                      {e.responsavel.name}
                    </span>
                    {TemPermissao(context.userLogged, permissionModule, "editar") && (
                      <button
                        type="button"
                        className="text-azul-400 duration-300"
                        onClick={() => editNote(e)}
                      >
                        <IconEdit />
                      </button>
                    )}
                    {TemPermissao(context.userLogged, permissionModule, "remover") && (
                      <button
                        type="button"
                        className="text-azul-400 hover:text-red-500 duration-300"
                        onClick={() => deleteNote(e.id)}
                      >
                        <IconDelete />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => toggleOpenDescriptionNote(e.id)}
                    >
                      {notaIsOpen[e.id] ? <IconIsOpen /> : <IconIsClose />}
                    </button>
                  </div>
                  <AnimationOpen isOpen={notaIsOpen}>
                    {notaIsOpen[e.id] && (
                      <div className="bg-white p-4 rounded-3xl mt-5 min-h-[195px]">
                        {e.description &&
                          e.description !== "Áudio" &&
                          e.description !== "Arquivo(s) anexado(s)" && (
                            <div className="mb-4 border-b-2 pb-4 whitespace-pre-wrap break-words">
                              {renderDescription(e.description)}
                            </div>
                          )}

                        {/* Arquivos de áudio anexados */}
                        {noteFiles.some((f: any) => {
                          const name = (f.name || "").toLowerCase();
                          return [
                            ".webm",
                            ".mp3",
                            ".wav",
                            ".ogg",
                            ".m4a",
                            ".aac",
                          ].some((ext) => name.endsWith(ext));
                        }) && (
                          <div className="mb-4 border-b-2 pb-4">
                            <AudioPlayer
                              files={noteFiles
                                .filter((f: any) => {
                                  const name = (f.name || "").toLowerCase();
                                  return [
                                    ".webm",
                                    ".mp3",
                                    ".wav",
                                    ".ogg",
                                    ".m4a",
                                    ".aac",
                                  ].some((ext) => name.endsWith(ext));
                                })
                                .map((f: any) => ({
                                  url: f.path,
                                  name: f.name,
                                  size: f.size
                                    ? parseInt(f.size.toString())
                                    : undefined,
                                }))}
                              isOwnMessage={false}
                            />
                          </div>
                        )}

                        {/* Outros arquivos anexados */}
                        {noteFiles
                          .filter((f: any) => {
                            const name = (f.name || "").toLowerCase();
                            return ![
                              ".webm",
                              ".mp3",
                              ".wav",
                              ".ogg",
                              ".m4a",
                              ".aac",
                            ].some((ext) => name.endsWith(ext));
                          })
                          .map((f: any, idx: number) => (
                            <a
                              key={idx}
                              href={f.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-cinza-100 rounded-lg px-3 py-1.5 text-xs mb-1 hover:bg-cinza-200 transition-colors"
                            >
                              <FileIcon
                                size={14}
                                className="text-cinza-500 flex-shrink-0"
                              />
                              <span className="flex-1 truncate">{f.name}</span>
                            </a>
                          ))}

                        <ComentariosTarefa
                          data={data}
                          parentId={e.id}
                          type={"nota"}
                        />
                      </div>
                    )}
                  </AnimationOpen>
                </div>
              );
            })}
        </div>
      </div>
      {newNote && (
        <>
          <div
            className="bg-black/30 absolute top-0 bottom-0 right-0 left-0 z-10 w-full h-[1000px] sm:h-full"
            onClick={resetForm}
          />
          <div className="absolute top-0 right-0 left-0 bottom-0 flex justify-center items-center h-[1000px] sm:h-full">
            <div className="bg-white rounded-3xl p-7 flex flex-col gap-2 max-w-md w-full z-20 relative">
              <span className="absolute -top-4 right-0 bg-azul-400 text-white text-sm px-5 py-1 rounded-2xl">
                {isEdit
                  ? getNomeDepartamentoById(watch("id_departamento"))
                  : getNomeDepartamentoById(departamentoActive)}
              </span>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-2"
              >
                <input
                  type="text"
                  placeholder={t("notesModule.form.title.placeholder")}
                  className="bg-cinza-100 rounded-xl text-center py-1"
                  required
                  {...register("title")}
                />
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <ReactQuill
                      {...field}
                      placeholder={t("notesModule.form.description.placeholder")}
                      className="bg-white w-full h-32 mb-16"
                      theme="snow"
                    />
                  )}
                />

                {/* Preview de arquivos selecionados */}
                {selectedFiles && selectedFiles.length > 0 && (
                  <div className="space-y-1">
                    {Array.from(selectedFiles).map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 bg-cinza-100 rounded-lg px-3 py-1.5 text-xs"
                      >
                        <FileIcon
                          size={14}
                          className="text-cinza-500 flex-shrink-0"
                        />
                        <span className="flex-1 truncate">{file.name}</span>
                        <span className="text-cinza-500 flex-shrink-0">
                          {file.size >= 1024 * 1024
                            ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                            : `${(file.size / 1024).toFixed(1)} KB`}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-cinza-500 hover:text-red-500 transition-colors flex-shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Arquivo existente da API (modo edição) */}
                {existingApiFile &&
                  (() => {
                    const audioExts = [
                      ".webm",
                      ".mp3",
                      ".wav",
                      ".ogg",
                      ".m4a",
                      ".aac",
                    ];
                    const isAudio = audioExts.some((ext) =>
                      (existingApiFile.name || "").toLowerCase().endsWith(ext),
                    );
                    return (
                      <div className="flex items-center gap-2 bg-cinza-100 rounded-xl px-3 py-2">
                        {isAudio ? (
                          <audio
                            src={existingApiFile.path}
                            controls
                            className="flex-1 h-8"
                            style={{ accentColor: "#4A90D9" }}
                          />
                        ) : (
                          <>
                            <FileIcon
                              size={14}
                              className="text-cinza-500 flex-shrink-0"
                            />
                            <span className="flex-1 truncate text-xs">
                              {existingApiFile.name}
                            </span>
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setExistingApiFile(null);
                            setRemovedExistingFile(true);
                          }}
                          className="text-cinza-500 hover:text-red-500 transition-colors flex-shrink-0"
                          title="Remover"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })()}

                {/* Preview do áudio gravado */}
                {audioBlobUrl && !showAudioRecorder && (
                  <div className="flex items-center gap-2 bg-cinza-100 rounded-xl px-3 py-2">
                    <audio
                      src={audioBlobUrl}
                      controls
                      className="flex-1 h-8"
                      style={{ accentColor: "#4A90D9" }}
                    />
                    <button
                      type="button"
                      onClick={removeAudio}
                      className="text-cinza-500 hover:text-red-500 transition-colors flex-shrink-0"
                      title="Remover áudio"
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                {responsaveisList.length > 0 && (
                  <Select
                    placeholder={t("tasks.fields.responsible")}
                    className="w-full"
                    isMulti
                    defaultValue={watch("list_responsaveis")}
                    onChange={(value: any) =>
                      setValue("list_responsaveis", value)
                    }
                    options={responsaveisList}
                    noOptionsMessage={() =>
                      t("resumoDesk.notes.no_responsibles_available")
                    }
                    formatOptionLabel={(user: any) => (
                      <div className="flex gap-2 items-start">
                        <img
                          className="w-6 h-6 rounded-full"
                          src={user.image ?? ImageGeneric}
                          alt={user?.label}
                          title={user?.label}
                        />
                        <span>{user?.label}</span>
                      </div>
                    )}
                  />
                )}

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    className="w-5 h-5"
                    {...register("is_public")}
                    defaultChecked={!isEdit}
                  />
                  <span>{t("tasks.fields.public")}</span>
                </label>

                {/* Barra de ações — anexar arquivo e gravar áudio */}
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  {!selectedFiles &&
                    !audioBlob &&
                    !showAudioRecorder &&
                    !existingApiFile && (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-1.5 text-cinza-500 hover:text-azul-400 transition-colors text-xs py-1 px-2 rounded-lg border border-cinza-200 hover:border-azul-400"
                        title="Anexar arquivo"
                      >
                        <Paperclip size={14} />
                        <span>Anexar</span>
                      </button>
                    )}
                  {!selectedFiles &&
                    !audioBlob &&
                    !showAudioRecorder &&
                    !existingApiFile && (
                      <button
                        type="button"
                        onClick={() => setShowAudioRecorder(true)}
                        className="flex items-center gap-1.5 text-cinza-500 border-cinza-200 hover:text-azul-400 hover:border-azul-400 transition-colors text-xs py-1 px-2 rounded-lg border"
                        title="Gravar áudio"
                      >
                        <Mic size={14} />
                        <span>Áudio</span>
                      </button>
                    )}
                </div>

                {/* NoteAudioRecorder dentro do form mas com todos os botões type=button */}
                {showAudioRecorder && (
                  <NoteAudioRecorder
                    onAudioReady={handleAudioReady}
                    onCancel={() => setShowAudioRecorder(false)}
                  />
                )}

                <button
                  className={`py-1 rounded-xl text-white max-w-xs w-full mx-auto transition-opacity ${
                    isSubmiting
                      ? "bg-azul-400 opacity-50 cursor-not-allowed"
                      : "bg-azul-400 hover:opacity-90"
                  }`}
                  type="submit"
                  disabled={isSubmiting}
                >
                  {isSubmiting
                    ? t("resumoDesk.notes.creating_note")
                    : t("resumoDesk.notes.create_note")}
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </>
  );
}
